import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CreditCard,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';

interface Subscription {
  id: string;
  email: string;
  displayName: string | null;
  stripeCustomerId: string | null;
  subscriptionStatus: string;
  subscriptionPlan: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

interface FailedPaymentAccount {
  id: string;
  email: string;
  displayName: string | null;
  stripeCustomerId: string | null;
  subscriptionPlan: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  billingEvents: { id: string; type: string; createdAt: string }[];
}

interface BillingEvent {
  id: string;
  accountId: string | null;
  stripeEventId: string;
  type: string;
  createdAt: string;
  account: { email: string; displayName: string | null } | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type Tab = 'subscriptions' | 'failed' | 'events';

const statusColors: Record<string, string> = {
  ACTIVE: 'text-green-400 bg-green-400/10',
  PAST_DUE: 'text-red-400 bg-red-400/10',
  CANCELED: 'text-gray-400 bg-gray-400/10',
  INCOMPLETE: 'text-yellow-400 bg-yellow-400/10',
};

export default function HealthSubscriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) || 'subscriptions');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [failedAccounts, setFailedAccounts] = useState<FailedPaymentAccount[]>([]);
  const [billingEvents, setBillingEvents] = useState<BillingEvent[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [totalFailedPayments, setTotalFailedPayments] = useState(0);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || '';

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(page));

    if (tab === 'subscriptions') {
      if (status) params.set('status', status);
      fetch(`/api/admin/health/subscriptions?${params}`)
        .then((r) => r.json())
        .then((data) => {
          setSubscriptions(data.subscriptions || []);
          setPagination(data.pagination);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (tab === 'failed') {
      fetch(`/api/admin/health/failed-payments?${params}`)
        .then((r) => r.json())
        .then((data) => {
          setFailedAccounts(data.accounts || []);
          setTotalFailedPayments(data.totalFailedPayments || 0);
          setPagination(data.pagination);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      params.set('limit', '50');
      fetch(`/api/admin/health/billing-events?${params}`)
        .then((r) => r.json())
        .then((data) => {
          setBillingEvents(data.events || []);
          setPagination(data.pagination);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [tab, page, status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const switchTab = (newTab: Tab) => {
    setTab(newTab);
    const params = new URLSearchParams();
    params.set('tab', newTab);
    setSearchParams(params);
  };

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const PaginationControls = () =>
    pagination && pagination.totalPages > 1 ? (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
        <p className="text-sm text-gray-400">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
            className="p-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page >= pagination.totalPages}
            className="p-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Subscriptions & Billing</h1>
          <p className="text-gray-400 mt-1">Manage subscriptions and review payment events</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-800 rounded-lg p-1 w-fit">
        {([
          { key: 'subscriptions' as Tab, label: 'Subscriptions', icon: CreditCard },
          { key: 'failed' as Tab, label: 'Failed Payments', icon: AlertTriangle },
          { key: 'events' as Tab, label: 'Billing Events', icon: FileText },
        ]).map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-primary-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <>
          {/* Subscriptions Tab */}
          {tab === 'subscriptions' && (
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              {/* Status filter */}
              <div className="px-4 py-3 border-b border-gray-700 flex gap-2">
                {['', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'INCOMPLETE'].map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      if (s) params.set('status', s);
                      else params.delete('status');
                      params.set('page', '1');
                      params.set('tab', 'subscriptions');
                      setSearchParams(params);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      status === s
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {s || 'All'}
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Account</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Plan</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Period End</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Last Login</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <Link to={`/health/accounts/${sub.id}`} className="text-primary-400 hover:text-primary-300 font-medium">
                            {sub.email}
                          </Link>
                          {sub.displayName && <p className="text-xs text-gray-500">{sub.displayName}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[sub.subscriptionStatus] || ''}`}>
                            {sub.subscriptionStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">{sub.subscriptionPlan || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {sub.subscriptionCurrentPeriodEnd
                            ? new Date(sub.subscriptionCurrentPeriodEnd).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {sub.lastLoginAt ? new Date(sub.lastLoginAt).toLocaleDateString() : 'Never'}
                        </td>
                      </tr>
                    ))}
                    {subscriptions.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No subscriptions found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationControls />
            </div>
          )}

          {/* Failed Payments Tab */}
          {tab === 'failed' && (
            <div>
              {totalFailedPayments > 0 && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="text-sm text-red-400 font-medium">
                      {totalFailedPayments} total failed payment event{totalFailedPayments !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-400">
                      {pagination?.total || 0} account{(pagination?.total || 0) !== 1 ? 's' : ''} currently past due
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-gray-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Account</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Plan</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Period End</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Last Failed</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {failedAccounts.map((acct) => (
                        <tr key={acct.id} className="hover:bg-gray-700/30">
                          <td className="px-4 py-3">
                            <Link to={`/health/accounts/${acct.id}`} className="text-primary-400 hover:text-primary-300 font-medium">
                              {acct.email}
                            </Link>
                            {acct.displayName && <p className="text-xs text-gray-500">{acct.displayName}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">{acct.subscriptionPlan || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {acct.subscriptionCurrentPeriodEnd
                              ? new Date(acct.subscriptionCurrentPeriodEnd).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {acct.billingEvents[0]
                              ? new Date(acct.billingEvents[0].createdAt).toLocaleString()
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/health/accounts/${acct.id}`}
                              className="text-sm text-primary-400 hover:text-primary-300"
                            >
                              Manage
                            </Link>
                          </td>
                        </tr>
                      ))}
                      {failedAccounts.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                            No past-due accounts
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationControls />
              </div>
            </div>
          )}

          {/* Billing Events Tab */}
          {tab === 'events' && (
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Event Type</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Account</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Stripe Event ID</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-400 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {billingEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${
                            event.type.includes('failed') ? 'text-red-400' :
                            event.type.includes('succeeded') ? 'text-green-400' :
                            event.type.includes('deleted') ? 'text-gray-400' :
                            'text-white'
                          }`}>
                            {event.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {event.account ? (
                            <Link to={`/health/accounts/${event.accountId}`} className="text-primary-400 hover:text-primary-300">
                              {event.account.email}
                            </Link>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                          {event.stripeEventId.slice(0, 24)}...
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(event.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {billingEvents.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-gray-500">No billing events</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationControls />
            </div>
          )}
        </>
      )}
    </div>
  );
}
